import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  VStack,
  useToast,
  Heading,
  Text,
  Code,
  Flex,
  Spinner,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FiZap, FiSave, FiEdit3 } from 'react-icons/fi';
import { API } from '../../../utils/apiBase';

export default function AIGeneratorTab() {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [type, setType] = useState('devotional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [editableJson, setEditableJson] = useState('');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({ title: 'Escribe un prompt', status: 'warning' });
      return;
    }
    setIsGenerating(true);
    setGeneratedData(null);
    setEditableJson('');

    try {
      const res = await fetch(`${API}/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, type }),
      });
      const resData = await res.json();

      if (!res.ok || !resData.ok) {
        throw new Error(resData.error || 'Error al generar');
      }

      setGeneratedData(resData.data);
      setEditableJson(JSON.stringify(resData.data, null, 2));
      toast({
        title: '¡Contenido generado!',
        description: 'Revisa la vista previa antes de publicarlo.',
        status: 'success',
      });
    } catch (error) {
      toast({
        title: 'Error de IA',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const slugify = (text) => {
    return (text || '').toLowerCase()
      .replace(/[\u0300-\u036f]/g, "")
      .normalize("NFD")
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const handleSave = async () => {
    try {
      // Validate JSON first
      const dataToSave = JSON.parse(editableJson);
      setIsSaving(true);

      let itemsToSave = Array.isArray(dataToSave) ? dataToSave : [dataToSave];
      let endpoint = '';

      if (type === 'devotional' || type === 'devotional_series') {
        endpoint = '/devotionals';
        itemsToSave = itemsToSave.map((item, index) => {
          if (!item.slug) item.slug = slugify(item.title);
          if (!item.author) item.author = 'Equipo Pastoral';
          if (!item.date) {
            const date = new Date();
            date.setDate(date.getDate() + index); // Un día distinto por cada parte
            item.date = date.toISOString().split('T')[0];
          }
          if (type === 'devotional' && (!item.serie || item.serie === item.title)) {
            item.serie = null;
          }
          return item;
        });
      } else if (type === 'study' || type === 'study_series') {
        endpoint = '/studies';
        itemsToSave = itemsToSave.map(item => {
          if (type === 'study' && (!item.serie || item.serie === item.tema)) {
            item.serie = null;
          }
          return item;
        });
      } else if (type === 'verse') {
        endpoint = '/verses';
      }

      // Guardar todos los items secuencialmente o en paralelo
      const savePromises = itemsToSave.map(item => {
        return fetch(`${API}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        }).then(async res => {
          const resData = await res.json();
          if (!res.ok) throw new Error(resData.error || 'Error al guardar');
          return resData;
        });
      });

      await Promise.all(savePromises);

      toast({
        title: 'Publicado correctamente',
        description: 'El contenido ya está disponible en la app.',
        status: 'success',
      });
      
      setGeneratedData(null);
      setEditableJson('');
      setPrompt('');
    } catch (error) {
      toast({
        title: 'Error al publicar',
        description: error.message,
        status: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="md" mb={2}>Generador con IA</Heading>
          <Text fontSize="sm" color="gray.500">
            Describe el tema o la idea y Gemini generará el contenido estructurado listo para publicar en la base de datos.
          </Text>
        </Box>

        <FormControl isRequired>
          <FormLabel>¿Qué deseas generar?</FormLabel>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="devotional">Devocional Individual</option>
            <option value="devotional_series">Serie de Devocionales (Múltiples partes)</option>
            <option value="study">Estudio Bíblico Individual</option>
            <option value="study_series">Serie de Estudios Bíblicos (Múltiples partes)</option>
            <option value="verse">Versículo Diario</option>
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>Instrucciones (Prompt)</FormLabel>
          <Textarea
            placeholder="Ej: Escribe un devocional sobre la fe en tiempos de crisis usando Hebreos 11:1..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
          />
        </FormControl>

        <Button
          colorScheme="blue"
          leftIcon={<FiZap />}
          onClick={handleGenerate}
          isLoading={isGenerating}
          loadingText="Pensando..."
          alignSelf="flex-start"
        >
          Generar con Gemini
        </Button>

        {generatedData && (
          <Box mt={6} p={4} borderWidth={1} borderRadius="md" bg="gray.50" _dark={{ bg: 'gray.800' }}>
            <Flex align="center" gap={2} mb={4}>
              <Icon as={FiEdit3} />
              <Heading size="sm">Vista Previa (Puedes editar el JSON directamente)</Heading>
            </Flex>
            
            <Textarea
              value={editableJson}
              onChange={(e) => setEditableJson(e.target.value)}
              fontFamily="monospace"
              fontSize="sm"
              rows={15}
              mb={4}
            />

            <Button
              colorScheme="green"
              leftIcon={<FiSave />}
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Guardando..."
            >
              Publicar a la Base de Datos
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
